import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import {
  fetchAdminUnits,
  fetchAdminRooms,
  normalizeUnit,
  normalizeRoom,
  fetchAdminDashboardKpis,
  fetchAdminTenanciesAll,
  fetchAdminCashDepositsHeld,
  fetchAdminCashDepositsHeldDetails,
  normalizeFetchError,
  sanitizeClientErrorMessage,
} from "../../api/adminData";
import { monthEndDateString } from "./uebersicht/uebersichtHelpers";
import CashDepositDetailsModal from "./uebersicht/CashDepositDetailsModal";
import PeriodPicker from "./uebersicht/PeriodPicker";
import usePeriodParams from "./uebersicht/usePeriodParams";
import { DetailTabs, DetailTabPanel } from "@/components/ui/DetailTabs";
import DepositsTab from "../../components/admin/companyDeposits/DepositsTab";
import OverviewTab from "../../components/admin/companyOverview/OverviewTab";
import FinanceTab from "../../components/admin/companyFinance/FinanceTab";
import PerformanceTab from "../../components/admin/companyPerformance/PerformanceTab";


const UEBERSICHT_TABS = [
  { id: "overview", label: "Übersicht" },
  { id: "finanzen", label: "Finanzen" },
  // K3c — beside Finanzen because both answer for the current state, while
  // Performance and Rechnungen answer for the selected period. The trigger says
  // "Kautionen" alone; the panel carries the full "Kautionen & Sicherheiten".
  { id: "kautionen", label: "Kautionen" },
  { id: "performance", label: "Performance" },
  { id: "rechnungen", label: "Rechnungen" },
];

/**
 * Tabs that are doorways rather than panels (Rechnungen 2.0 — R5).
 *
 * "Rechnungen" used to open an inline panel that aggregated whatever page of
 * invoices the client happened to have loaded, without knowing whether they came
 * from Bexio or from Vantio. `/admin/rechnungen-dashboard` asks the server for
 * one named source and reads its totals from R4, so the two answered the same
 * question with different numbers. The trigger now leads there.
 *
 * There is no longer a second panel to leave behind: the inline dashboard this
 * trigger used to open has been deleted, and the tab's panel is an empty target
 * that exists only so the trigger keeps a valid `aria-controls`.
 */
const TAB_DESTINATIONS = {
  rechnungen: "/admin/rechnungen-dashboard",
};

/** The tab list is the only list: a second one would drift the moment a tab is
 *  added, which is exactly when this check matters. A tab that navigates is
 *  never an initial value — landing on it would show the panel it replaced. */
function resolveInitialTab(requested) {
  if (TAB_DESTINATIONS[requested]) return "overview";
  return UEBERSICHT_TABS.some((tab) => tab.id === requested)
    ? requested
    : "overview";
}

export default function AdminUebersichtPage() {
  const navigate = useNavigate();
  const [units, setUnits] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [tenancies, setTenancies] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [kpisLoading, setKpisLoading] = useState(true);
  const [kpisError, setKpisError] = useState("");
  const [cashDepositsSnapshot, setCashDepositsSnapshot] = useState(null);
  const [cashDepositModalOpen, setCashDepositModalOpen] = useState(false);
  const [cashDepositDetails, setCashDepositDetails] = useState(null);
  const [cashDepositDetailsLoading, setCashDepositDetailsLoading] = useState(false);
  const [cashDepositDetailsError, setCashDepositDetailsError] = useState("");
  const [operationsLoadError, setOperationsLoadError] = useState("");
  // P4: `useMonthlyCharts` is no longer called here. Its remaining output, the
  // six-month `belegungChartData`, fed the "Belegung Zimmer" chart in the
  // Performance panel — a count of rooms occupied on each month's LAST DAY.
  // Performance 2.0 measures room-DAYS across the whole month, so the two are
  // different axes, and the redesigned tab answers the question on its own
  // contract instead of carrying the old series alongside it. The hook file and
  // its tests are untouched and now have no consumer; retiring them is a
  // separate cleanup outside this slice's file plan.
  // 6A.1: unified period model. mode "month" → single month (from == to, the
  // default → ZERO behavior change). mode "all" → "Gesamte Zeit" (from null =
  // unbounded-below, to = current month). Presets/custom (6A.3) build on this.
  //
  // P4 MOVED THIS INTO THE URL. It was a `useState` seeded from
  // `currentBusinessPeriod()`, so the selection died on reload, could not be
  // linked, and Back left the page instead of returning to the previous period.
  // `usePeriodParams` derives the same object from the query string and defaults
  // to the same current Swiss business month when the address carries no period,
  // so an untouched page behaves exactly as before. There is deliberately no
  // local copy and no synchronising effect: with one source there is no second
  // one to fall out of step with, and a period change stays ONE render and one
  // request round.
  const { period: kpisPeriod, setPeriod: setKpisPeriod } = usePeriodParams();
  // P3b REMOVED THE OVERVIEW'S PERIOD COLLAPSE. This is where
  // `overviewPeriod = kpisPeriod?.to || ...` used to stand: one line that reduced a
  // multi-month selection to its upper bound before the tab ever saw it, so a filter
  // reading "April – September 2026" asked five endpoints for September alone. The
  // tab now receives the same `{mode, from, to}` Performance and Finanzen receive and
  // resolves it against the P3a window contracts itself.
  // A caller may ask for a specific tab through navigation state — the deposits
  // page's "Zurück zu Finanzen" does. The tabs are local state with no URL of
  // their own, so without this the link would land on Übersicht and contradict
  // its own label. Read once as the initial value: this selects the landing tab,
  // it does not lock the user out of switching afterwards.
  const location = useLocation();
  // An unrecognised value used to be taken at face value, and since no trigger
  // and no panel then matched it, the page rendered its header over an empty
  // body. Anything that is not a real tab lands on Übersicht instead.
  const [activeTab, setActiveTab] = useState(() =>
    resolveInitialTab(location.state ? location.state.tab : undefined)
  );

  // A doorway does not also open the room behind it: the tab that navigates
  // never becomes the selected one, so returning here lands on Übersicht rather
  // than on a panel the reader was deliberately routed away from.
  const selectTab = useCallback(
    (next) => {
      const destination = TAB_DESTINATIONS[next];
      if (destination) {
        navigate(destination);
        return;
      }
      setActiveTab(next);
    },
    [navigate]
  );

  // 6A.1 derived period scalars. The body/compat-shim always uses the to-month;
  // isRange (mode !== "month") drives range-mode display. from is null for "all".
  const isRange = kpisPeriod.mode !== "month";
  const toY = kpisPeriod.to.year;
  const toM = kpisPeriod.to.month;
  const fromY = kpisPeriod.from ? kpisPeriod.from.year : null;
  const fromM = kpisPeriod.from ? kpisPeriod.from.month : null;
  // 6A.3's `toMonthLabel` moved into `PerformanceTab` with the two cards that
  // were its only readers. It is still the SAME rule -- the point-in-time cards
  // stay on the range's to-month and say "· Stand <Monat>" -- computed beside
  // the month the tab actually requested rather than beside the picker.

  useEffect(() => {
    setOperationsLoadError("");
    // P4: `fetchAdminOccupancy()` was the third member of this bundle and is
    // gone. It filled `occupancyApi`, a today-snapshot of room counts whose only
    // two readers were the Performance panel's unit badges and its "Leerstand"
    // footer. The redesigned tab reads room-day occupancy for the SELECTED month
    // from the Performance 2.0 contract instead, so the snapshot has no reader
    // left. `fetchAdminOccupancy` itself is untouched and keeps its consumers.
    Promise.all([
      fetchAdminUnits(),
      fetchAdminRooms(),
      fetchAdminTenanciesAll().catch((err) => {
        console.error("AdminUebersichtPage: fetchAdminTenanciesAll failed", err);
        Sentry.captureException(err);
        return [];
      }),
    ])
      .then(([unitsData, roomsData, tenanciesData]) => {
        setUnits(unitsData.map(normalizeUnit));
        setRooms(Array.isArray(roomsData) ? roomsData.map(normalizeRoom) : []);
        setTenancies(Array.isArray(tenanciesData) ? tenanciesData : []);
      })
      .catch((e) => {
        setTenancies([]);
        setOperationsLoadError(
          sanitizeClientErrorMessage(
            normalizeFetchError(e, "Betriebsdaten konnten nicht geladen werden.").message,
            "Betriebsdaten konnten nicht geladen werden."
          )
        );
      });
  }, []);

  // S4: the page's OWN `/dashboard/profit` effect that used to live here is
  // gone. After "Gesamtgewinn" moved to the Performance tab as "Erwartetes
  // Objektergebnis" -- on the classified operating costs (R1) instead of the raw
  // sum -- nothing on this page rendered a value from that payload any more.
  // `operationsStats` still computed `currentRevenue` and `runningCosts` from
  // it, but no JSX read either, so the request bought one round trip per month
  // change and produced nothing visible.
  //
  // When S4 wrote this it added, in capitals, that the endpoint was STILL
  // reached from this page: `useMonthlyCharts` called it six more times on
  // mount, one per month, feeding only that hook's `financeChartData` -- a
  // series that had already lost its reader. S4 left those six in place because
  // they sat in a hook it did not touch.
  //
  // P4-pre removed them. `/dashboard/profit` is no longer requested from this
  // page at all, and `AdminUebersichtPage.test.js` asserts exactly that.

  // P4-pre: the per-unit `fetchAdminUnitCosts` effect that used to stand here is
  // gone. It mapped every unit to its own request -- one HTTP call per unit on
  // every page load, a real N+1 -- and wrote the results into
  // `unitCostsByUnitId`, a state this page never read: no JSX, no memo, no
  // derived value. Whatever it once fed had already moved elsewhere.
  //
  // `fetchAdminUnitCosts` itself is untouched and keeps its consumers
  // (AdminApartmentsPage, AdminExpensesPage, AdminBreakEvenPage,
  // AdminUnitDetailPage), each of which has its own identically named state and
  // does read it.

  useEffect(() => {
    setKpisLoading(true);
    setKpisError("");
    const y = toY;
    const mo = toM;
    const onDate = monthEndDateString(y, mo);
    Promise.all([
      // Period → kpis params (three-way):
      //  - "all"    → range_mode="all" (ALL invoices, no bound; 6A.1.1)
      //  - "custom" → range_mode="custom" + bounded from_*/to_* (6A.3: real
      //               Gewinn/Marge, range-aware splits/leerstand)
      //  - "month"  → legacy year/month
      // The other four fetches stay to-month (aktueller Stand compat shim).
      fetchAdminDashboardKpis(
        kpisPeriod.mode === "all"
          ? { range_mode: "all" }
          : kpisPeriod.mode === "custom"
            ? {
                range_mode: "custom",
                from_year: fromY,
                from_month: fromM,
                to_year: toY,
                to_month: toM,
              }
            : { year: y, month: mo }
      ),
      fetchAdminCashDepositsHeld(),
      // S4: `fetchAdminProfitRankedByMargin` used to be the third member of this
      // bundle and fired on every page load, for a tab nobody had opened. The
      // Performance tab now owns its own gated request; the legacy endpoint is
      // untouched and keeps its other consumers (KPI C.5, /admin/performance).
    ])
      .then(([kpiJson, cashDepositsJson]) => {
        setKpis(kpiJson);
        setCashDepositsSnapshot(cashDepositsJson);
        setKpisLoading(false);
      })
      .catch((e) => {
        setKpisError(
          sanitizeClientErrorMessage(e.message, "KPI-Daten konnten nicht geladen werden.")
        );
        setKpis(null);
        setCashDepositsSnapshot(null);
        setKpisLoading(false);
      });
  }, [kpisPeriod.mode, isRange, fromY, fromM, toY, toM]);
  // P4: `operationsStats` stood here and is gone with `occupancyApi`, its only
  // source. Of its five room counters exactly one ever reached the screen —
  // `freeRooms`, in the "Leerstand" footer under the Performance panel's unit
  // list — and that footer stated a count of rooms free TODAY beneath figures
  // about a selected month. The redesigned Auslastung card answers the same
  // question on the month's own room-day basis.
  //
  // S4 had already emptied this memo of its money figures: `currentRevenue`,
  // `runningCosts` and `currentProfit` went with the `/dashboard/profit` request
  // they came from. The invoice fold that followed it — `invoiceStats` and
  // `latestInvoices` — is gone too, with the panel that read them.
  // Step 4.4: totalFullPotential is a simple sum of room target prices (Co-Living)
  // and unit.tenantPriceMonthly (Apartments). This is raw model-field aggregation
  // from already-loaded data, not a KPI calculation — no backend endpoint needed.
  const totalFullPotential = useMemo(() => {
    if (!Array.isArray(units)) return 0;
    return units.reduce((sum, unit) => {
      if (String(unit.type || "").trim() === "Co-Living") {
        const unitRooms = (rooms || []).filter(
          (r) => String(r.unitId || r.unit_id) === String(unit.id)
        );
        return sum + unitRooms.reduce((s, r) => s + Number(r.priceMonthly || 0), 0);
      }
      return sum + Number(unit.tenantPriceMonthly || 0);
    }, 0);
  }, [units, rooms]);


  const openCashDepositDetails = () => {
    setCashDepositModalOpen(true);
    setCashDepositDetails(null);
    setCashDepositDetailsError("");
    setCashDepositDetailsLoading(true);
    fetchAdminCashDepositsHeldDetails()
      .then((d) => setCashDepositDetails(d))
      .catch((e) =>
        setCashDepositDetailsError(
          e?.message || "Barkautionen-Details konnten nicht geladen werden."
        )
      )
      .finally(() => setCashDepositDetailsLoading(false));
  };

  // S4: the weakest unit used to be derived HERE, as `ranked_units[-1]`, while
  // the server's own `worst_unit_id` sat unread in the same payload. It is now
  // resolved from that id inside `PerformanceTab` — one decision, taken once,
  // on the server.

  return (
    <div
      data-testid="admin-dashboard-page"
      className="min-h-screen bg-gray-50 text-gray-900 dark:bg-[#080a0f] dark:text-[#edf0f7]"
    >
      <header className="sticky top-0 z-30 flex h-[50px] items-center justify-between border-b border-gray-200 bg-white px-6 backdrop-blur-md dark:border-[#1c2035] dark:bg-[#0c0e15]">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="truncate text-[14px] font-medium text-gray-900 dark:text-[#edf0f7]">Unternehmensübersicht</span>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {/* 6A.3b: period picker (presets + custom Von–Bis) → kpisPeriod {mode,from,to} */}
          <PeriodPicker value={kpisPeriod} onChange={setKpisPeriod} />
          <span className="inline-flex items-center rounded-[6px] border border-gray-200 bg-slate-100 px-3 py-1 text-[11px] text-gray-500 dark:border-[#1c2035] dark:bg-[#141720] dark:text-[#8892b0]">
            Live-KPI
          </span>
          <span className="inline-flex items-center rounded-[6px] border border-sky-200 bg-sky-50 px-3 py-1 text-[11px] font-medium text-sky-600 dark:border-[rgba(91,156,246,0.25)] dark:bg-[rgba(91,156,246,0.1)] dark:text-[#5b9cf6]">
            Management-Ansicht
          </span>
        </div>
      </header>

      <div className="mx-auto max-w-[min(1400px,100%)] px-6 py-5">
        <DetailTabs tabs={UEBERSICHT_TABS} value={activeTab} onValueChange={selectTab} accent="orange">
          <DetailTabPanel tabId="overview" className="space-y-5">
            <OverviewTab
              period={kpisPeriod}
              active={activeTab === "overview"}
              cashDeposits={cashDepositsSnapshot}
              cashDepositsLoading={kpisLoading}
              onOpenCashDepositDetails={openCashDepositDetails}
            />

            {/* The dashboard's own assumptions and availability, from
                `/dashboard/kpis`. It sat in the Rechnungen panel for
                historical reasons and became unreachable the day that tab
                turned into a doorway; it describes THIS panel's figures, so
                it lives here now. Moved verbatim — same fields, same copy. */}
            {kpis && !kpisLoading && Array.isArray(kpis.assumptions) && kpis.assumptions.length > 0 ? (
              <div className="mt-6 rounded-[12px] border border-gray-200 bg-white p-[14px_18px] dark:border-[#1c2035] dark:bg-[#10121a]">
                <h3 className="mb-[10px] text-[10px] font-medium uppercase tracking-[0.7px] text-gray-500 dark:text-[#4a5070]">Annahmen &amp; Limitationen</h3>
                <div className="grid grid-cols-1 gap-x-[20px] gap-y-[2px] md:grid-cols-3">
                  {kpis.assumptions.map((a, i) => (
                    <div key={i} className="flex items-start gap-[7px] py-1 text-[10px] leading-[1.5] text-gray-500 dark:text-[#4a5070]">
                      <span className="mt-[6px] h-[3px] w-[3px] shrink-0 rounded-full bg-gray-400 dark:bg-[#4a5070]" />
                      <span>{a}</span>
                    </div>
                  ))}
                </div>
                {kpis.availability ? (
                  <p className="mt-3 text-[10px] leading-relaxed text-gray-500 dark:text-[#4a5070]">
                    Verfügbarkeit: Umsatz/Gewinn = {kpis.availability.revenue}; Leerstandstage = {kpis.availability.vacant_days}; Prognose ={" "}
                    {kpis.availability.forecast}; Gewinnschwelle = {kpis.availability.break_even}.
                  </p>
                ) : null}
            </div>
          ) : null}
          </DetailTabPanel>
          <DetailTabPanel tabId="finanzen" className="space-y-5">
            {/* The tenant deposit snapshot and its details modal are owned HERE
                and handed to both tabs. Finanzen shows the same figure under a
                different heading; loading it a second time would duplicate a
                live request and split the modal's ownership across two files. */}
            <FinanceTab
              period={kpisPeriod}
              active={activeTab === "finanzen"}
              cashDeposits={cashDepositsSnapshot}
              cashDepositsLoading={kpisLoading}
              onOpenCashDepositDetails={openCashDepositDetails}
            />
          </DetailTabPanel>
          <DetailTabPanel tabId="kautionen" className="space-y-5">
            {/* No `lazyMount` on the tab: this page gates with `active` and the
                hook's own `enabled`, and two mounting strategies on one page is
                one too many. A closed tab issues no deposit request. */}
            <DepositsTab active={activeTab === "kautionen"} />
          </DetailTabPanel>
          <DetailTabPanel tabId="performance" className="space-y-5">
            {/* P4: `PerformanceTab` is now the WHOLE panel. S4 had moved three
                surfaces into it and left the rest inline here; the remainder
                (Trend & Prognose, Einheiten · Auslastung, Belegung Zimmer, the
                Leerstand footer) has followed, either onto the Performance 2.0
                contract or into the tab's own "Weitere Kennzahlen" block.

                The legacy KPI payload is HANDED DOWN rather than re-fetched: the
                page already holds `kpis` for its own effect, and `totalFullPotential`
                is summed from units/rooms it already has. The tab issues exactly
                one request of its own, and only once it is opened. */}
            <PerformanceTab
              period={kpisPeriod}
              active={activeTab === "performance"}
              legacyKpis={kpis}
              legacyKpisLoading={kpisLoading}
              portfolioPotential={totalFullPotential}
              portfolioLoading={tenancies === null && !operationsLoadError}
            />
          </DetailTabPanel>
          {/* The Rechnungen tab is a doorway, not a panel: `selectTab`
              navigates to /admin/rechnungen-dashboard, which is the one
              invoice dashboard. The panel stays as an empty target so the
              trigger keeps a valid `aria-controls`. */}
          <DetailTabPanel tabId="rechnungen" />
        </DetailTabs>
      </div>
      <CashDepositDetailsModal
        open={cashDepositModalOpen}
        onClose={() => setCashDepositModalOpen(false)}
        loading={cashDepositDetailsLoading}
        error={cashDepositDetailsError}
        data={cashDepositDetails}
      />
    </div>
  );
}
